import { type SyntheticEvent, useCallback } from 'react';

import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { useActivePinPageSelector } from '@/packages/redux-state/store/slices/mapControls';
import { extractUndefined } from '@/packages/utils';

import { useListingByPinContext } from '../../context/ListingByPinContext';
import { type PaginationItem } from '../../types/pagination';

import { Pagination } from './components/Pagination';
import { usePagination } from './hooks/usePagination';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setActivePinPage: reduxActions.mapControls.setActivePinPage,
}));

export const PaginationByPinContainer = memoWithDisplayName(() => {
  const {
    listingProperties: { offersPerPage, aggregatedOffersCount: totalOffers, maxPage },
  } = useListingByPinContext();

  const activePinPage = useActivePinPageSelector();

  const { pages, nextPage, prevPage } = usePagination({
    maxPage: extractUndefined(maxPage),
    offersPerPage,
    page: activePinPage ?? 1,
    totalOffers,
  });

  const boundActions = useBoundActions();

  const handlePageClick = useCallback(
    async (_: SyntheticEvent<HTMLButtonElement>, paginationItem: PaginationItem) => {
      boundActions.setActivePinPage(paginationItem.page);
    },
    [boundActions],
  );

  if (!pages.length) {
    return null;
  }

  return <Pagination nextButton={nextPage} pages={pages} prevButton={prevPage} onClick={handlePageClick} />;
}, 'PaginationController');
